import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import { platformGet, platformSend, errorOf } from './platformApi';

/**
 * P07 — provisioning a company's database and watching it happen. Jobs run on the server's
 * provisioning queue; this panel only starts/resumes them and polls their recorded step.
 * A failed job resumes from its last checkpoint — completed steps are never redone.
 */

const STEP_LABELS = {
  queued: 'Queued',
  database_ready: 'Database ready',
  schema_migrated: 'Schema migrated',
  identity_marked: 'Identity marked',
  branches_mirrored: 'Branches mirrored',
  catalog_seeded: 'Permissions and roles seeded',
  activated: 'Activated',
};

function StepList({ job }) {
  const current = job.steps.indexOf(job.step);
  return (
    <ol className="space-y-1 text-sm" aria-label="Provisioning steps">
      {job.steps.slice(1).map((step, i) => {
        const index = i + 1;
        let icon = <Circle size={14} className="text-secondary-400" aria-hidden="true" />;
        let state = 'pending';
        if (index <= current) {
          icon = <CheckCircle2 size={14} className="text-green-600" aria-hidden="true" />;
          state = 'done';
        } else if (index === current + 1 && job.status === 'running') {
          icon = <Loader2 size={14} className="animate-spin text-primary-600" aria-hidden="true" />;
          state = 'in progress';
        } else if (index === current + 1 && job.status === 'failed') {
          icon = <XCircle size={14} className="text-red-600" aria-hidden="true" />;
          state = 'failed';
        }
        return (
          <li key={step} className="flex items-center gap-2">
            {icon}
            <span>{STEP_LABELS[step] || step}</span>
            <span className="sr-only">{state}</span>
          </li>
        );
      })}
    </ol>
  );
}

const STATUS_VARIANT = { succeeded: 'success', failed: 'danger', running: 'primary', pending: 'secondary' };

export default function ProvisioningPanel({ company, act, onChanged }) {
  const [jobs, setJobs] = useState([]);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setJobs(await platformGet(`/companies/${company.id}/provisioning`));
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  const active = jobs.find((j) => j.status === 'running' || j.status === 'pending');
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(async () => {
      await load();
      onChanged();
    }, 2000);
    return () => clearInterval(t);
  }, [active, load, onChanged]);

  const start = (path, body) => act(async () => {
    const job = await platformSend('post', path, body);
    await load();
    return job;
  });

  const latest = jobs[0];
  const canProvision = ['draft', 'provisioning_failed'].includes(company.status) && !active;
  const hasAttached = company.database && company.database.mode === 'external';

  return (
    <Card className="p-3 space-y-3 md:col-span-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">Provisioning</h2>
        {latest && <Badge size="sm" variant={STATUS_VARIANT[latest.status] || 'secondary'}>{latest.operation} · {latest.status}</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          {canProvision && (
            <Button size="sm" onClick={() => start(`/companies/${company.id}/provision`, { mode: 'managed' })}>Provision managed database</Button>
          )}
          {canProvision && hasAttached && (
            <Button size="sm" variant="secondary" onClick={() => start(`/companies/${company.id}/provision`, { mode: 'external' })}>Provision attached database</Button>
          )}
          {latest && latest.status === 'failed' && latest.attempts < latest.maxAttempts && (
            <Button size="sm" variant="secondary" onClick={() => start(`/provisioning/${latest.id}/retry`)}>Resume from last step</Button>
          )}
          {['active', 'maintenance'].includes(company.status) && !active && (
            <Button size="sm" variant="secondary" onClick={() => start(`/companies/${company.id}/migrate`)}>Apply pending migrations</Button>
          )}
        </div>
      </div>
      {err && <Alert variant="error">{err}</Alert>}
      {!latest && <p className="text-sm text-secondary-500">Not provisioned yet. Managed creates a database and a data-only user on the platform&apos;s server; attached uses the empty database you saved above.</p>}
      {latest && (
        <div className="grid gap-3 md:grid-cols-2">
          <StepList job={latest} />
          <div className="text-sm space-y-1">
            <div>Target schema: <code>{latest.targetVersion}</code></div>
            <div>Attempts: {latest.attempts} of {latest.maxAttempts}</div>
            {latest.lastError && <Alert variant="error" title="Last error">{latest.lastError}</Alert>}
          </div>
        </div>
      )}
    </Card>
  );
}
